import { supabase } from '@/integrations/supabase/client';

// Signed-in messages from the Contact and Support pages go into the
// customer's support chat (src/lib/supportChat.ts). They are STORED here
// first, and the page confirms only once this resolves: never "sent" for a
// message the server does not have (Codex on #1238).

/**
 * Store a signed-in customer's message in their support chat: the open (or
 * waiting-for-a-person) chat ticket, or a new one, exactly as the chat opens
 * it. Resolves only once the message row is stored; any failure throws, so
 * nothing is confirmed that the server does not have.
 */
export async function storeInSupportChat(userId: string, text: string): Promise<{ ticketId: string }> {
  const { data: existing, error: findError } = await supabase
    .from('support_tickets')
    .select('id, status')
    .eq('user_id', userId)
    .eq('channel', 'chat')
    .in('status', ['open', 'pending'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (findError) throw findError;
  let ticket = existing;
  if (!ticket) {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('is_helper')
      .eq('id', userId)
      .maybeSingle();
    if (profileError) throw profileError;
    const { data: created, error: createError } = await supabase
      .from('support_tickets')
      .insert({
        user_id: userId,
        user_role: profile?.is_helper ? 'helper' : 'client',
        channel: 'chat',
        subject: 'Chat Support',
        status: 'open',
      })
      .select('id, status')
      .single();
    if (createError || !created) throw createError ?? new Error('The support chat could not be opened.');
    ticket = created;
  }
  const { error: insertError } = await supabase.from('support_messages').insert({
    ticket_id: ticket.id,
    sender_id: userId,
    sender_type: 'user',
    message_text: text,
  });
  if (insertError) throw insertError;
  return { ticketId: ticket.id };
}

/**
 * Whether ai-support-chat has finished, with an error status. A timeout or a
 * dropped connection is not that: the request may still be running, and a
 * holding line posted now would take the place of its answer (Codex on
 * #1270). The server's sweep (support_holding_line_sweep) acknowledges those
 * after 3 minutes instead.
 */
export function assistantFinishedWithError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'FunctionsHttpError';
}

/**
 * Ask Kanfy AI to answer a stored message, as the chat does for a message
 * typed there: it answers, hands over to a person, or (once a person owns
 * the chat) answers a quick question or posts the holding line. Asked for
 * every message, handed over or not (Codex on #1270). Fire and forget: the
 * message is already stored. If Kanfy AI ends in an error, a chat a person
 * owns gets the holding line at once, as the chat itself does.
 */
export function askSupportAssistant(ticketId: string, text: string): void {
  void supabase.functions
    .invoke('ai-support-chat', { body: { ticketId, message: text } })
    .then(({ error }) => {
      if (!assistantFinishedWithError(error)) return undefined;
      return supabase.rpc('post_support_holding_line', { p_ticket_id: ticketId });
    })
    .then(() => undefined, () => undefined);
}
